/**
 * The EMSPCR Data Pipeline — canonical end-to-end definition.
 *
 * Top-down flow from Dispatch → Patient ID → History → Allergies →
 * Meds → Chief Complaint → Primary Assessment → Vitals → Secondary
 * Assessment → Protocol Activation → Interventions → Trauma/Special
 * Sets → Disposition → Mileage → Transfer of Care → Narrative → QA
 * Review → State Submission → Billing → Post.
 *
 * Each capture is keyed to: the NEMSIS element(s) it populates, the
 * downstream consumers that consume it, and the upstream sources
 * that pre-fill it (so redundancy is explicit). The visualization
 * page (DataPipelinePage.tsx) reads from here and proves a single
 * keystroke fans out to every downstream form that needs it —
 * "captured once, satisfies all."
 *
 * This is the operational schema, not the validator schema. The
 * conditional cascade engine (packages/compliance-rules) decides
 * WHEN a set / form is required; this file describes the data flow
 * that fulfills it.
 */

import type { ConditionalSetId, TriggeredFormId } from '@moquintrend/compliance-rules';

/* ============================================================
 * CONSUMERS — every downstream destination for captured data
 * ============================================================ */

export type ConsumerId =
  // Regulatory / compliance
  | 'nemsis_xml'             // NEMSIS v3.5.1 XML payload
  | 'riemsis_api'            // RI Dept of Health endpoint
  | 'ridoh_surveillance'     // RI public-health surveillance (overdose, stroke, etc.)
  | 'neris_fire'             // Federal NERIS fire incident report
  | 'dot_crash_set'          // NHTSA crash data (MVC sub-set)
  // Clinical operations
  | 'protocol_engine'        // RI Statewide Protocol activation
  | 'narrative_engine'       // DCHARTE / SBAR / SOAP / DISPATCH builder
  | 'toc_handoff'            // Hospital transfer-of-care packet
  | 'pci_alert'              // PCI center pre-arrival activation (STEMI)
  | 'stroke_alert'           // Stroke center pre-arrival activation
  | 'trauma_alert'           // Trauma center pre-arrival activation
  // Department / administrative
  | 'serious_unusual'        // Serious / Unusual Occurrence Report (Dept + RIDOH)
  | 'eap_outreach'           // Critical-incident peer support
  | 'me_referral'            // Medical examiner referral packet
  | 'audit_log'              // HIPAA per-record access log
  // Financial
  | 'comstar_billing'        // the agency billing partner export
  | 'cms_pcs'                // CMS Physician Certification Statement (non-emergency)
  | 'aba_burn_referral';     // American Burn Association transfer packet

export interface Consumer {
  id: ConsumerId;
  name: string;
  short: string;        // 3-4 char chip label
  category: 'regulatory' | 'clinical' | 'department' | 'financial';
  rationale: string;
  /**
   * If this consumer is conceptually a SUBSET of another (e.g. DOT
   * crash data is a subset of the trauma cascade), the parent's id
   * goes here. The diagram uses this to indent the child under its
   * parent in the right column and draw a small dotted connector.
   */
  parent?: ConsumerId;
}

/**
 * Trigger relationships between consumers and the upstream captures /
 * cascades that activate them. Drawn as DOTTED lines in the diagram so
 * they read as "this is a trigger" rather than "this is a regular data
 * flow." Solid lines = data fan-out; dotted lines = "filling X causes
 * Y to come into scope."
 */
export interface TriggerEdge {
  /** Capture id whose value drives the activation. */
  fromCapture: string;
  /** Consumer that comes into scope when the capture is set. */
  toConsumer: ConsumerId;
  /** Short label rendered on hover. */
  label: string;
}

export const TRIGGER_EDGES: TriggerEdge[] = [
  { fromCapture: 'situation.primary_impression', toConsumer: 'trauma_alert',
    label: 'Trauma terms in impression activate trauma cascade + triage criteria' },
  { fromCapture: 'situation.possible_injury', toConsumer: 'trauma_alert',
    label: 'Possible injury = Yes activates trauma cascade' },
  { fromCapture: 'situation.primary_impression', toConsumer: 'stroke_alert',
    label: 'Stroke / CVA impression activates LAMS + Cincinnati cascade' },
  { fromCapture: 'situation.primary_impression', toConsumer: 'pci_alert',
    label: 'STEMI impression activates 12-lead + PCI alert cascade' },
  { fromCapture: 'incident.types', toConsumer: 'neris_fire',
    label: 'Fire incident type activates NERIS submission' },
  { fromCapture: 'crash.vehicle_role', toConsumer: 'dot_crash_set',
    label: 'MVC vehicle role activates DOT crash subset' },
  { fromCapture: 'disposition.doa_pronouncement', toConsumer: 'me_referral',
    label: 'DOA pronouncement triggers ME referral packet' },
  { fromCapture: 'restraint.justification', toConsumer: 'serious_unusual',
    label: 'Restraint use triggers Serious / Unusual Occurrence' },
  { fromCapture: 'burn.tbsa', toConsumer: 'aba_burn_referral',
    label: 'Burn TBSA ≥10% activates ABA transfer packet' },
];

export const CONSUMERS: Record<ConsumerId, Consumer> = {
  nemsis_xml: {
    id: 'nemsis_xml', name: 'NEMSIS XML', short: 'NEM',
    category: 'regulatory',
    rationale: 'NEMSIS v3.5.1 XML payload — the canonical record. Every capture maps to one or more NEMSIS elements.',
  },
  riemsis_api: {
    id: 'riemsis_api', name: 'RIEMSIS Submission', short: 'RI',
    category: 'regulatory',
    rationale: 'RI Dept of Health licensure-required submission of every patient encounter.',
  },
  ridoh_surveillance: {
    id: 'ridoh_surveillance', name: 'RIDOH Surveillance', short: 'RDH',
    category: 'regulatory',
    rationale: 'Public-health surveillance flags (opioid overdose, stroke, behavioral, communicable disease).',
  },
  neris_fire: {
    id: 'neris_fire', name: 'NERIS Fire Report', short: 'NER',
    category: 'regulatory',
    rationale: 'Federal NERIS submission — required regardless of EMS findings when unit responds to a fire incident.',
  },
  dot_crash_set: {
    id: 'dot_crash_set', name: 'DOT Crash Data', short: 'DOT',
    category: 'regulatory',
    rationale: 'NHTSA crash surveillance (vehicle role, restraint, airbag, intrusion, ejection). Subset of the trauma cascade — only fires when MVC mechanism is set.',
    parent: 'trauma_alert',
  },
  protocol_engine: {
    id: 'protocol_engine', name: 'Protocol Engine', short: 'PRT',
    category: 'clinical',
    rationale: 'RI Statewide Protocols — activates intervention menu, dose pre-fill, scope-aware filtering.',
  },
  narrative_engine: {
    id: 'narrative_engine', name: 'Narrative Engine', short: 'NRR',
    category: 'clinical',
    rationale: 'Slot-based narrative assembler (DCHARTE / SBAR / SOAP / DISPATCH per Protocol 1.02).',
  },
  toc_handoff: {
    id: 'toc_handoff', name: 'Hospital ToC Packet', short: 'TOC',
    category: 'clinical',
    rationale: 'Transfer-of-care script + signed packet handed to receiving facility.',
  },
  pci_alert: {
    id: 'pci_alert', name: 'PCI Center Alert', short: 'PCI',
    category: 'clinical',
    rationale: 'Pre-arrival STEMI activation — triggered on 12-lead interpretation.',
  },
  stroke_alert: {
    id: 'stroke_alert', name: 'Stroke Center Alert', short: 'STR',
    category: 'clinical',
    rationale: 'Pre-arrival stroke activation — triggered on Cincinnati / LAMS positive.',
  },
  trauma_alert: {
    id: 'trauma_alert', name: 'Trauma Center Alert', short: 'TRM',
    category: 'clinical',
    rationale: 'Pre-arrival trauma activation — triggered on ACS 2021 red/yellow criteria.',
  },
  serious_unusual: {
    id: 'serious_unusual', name: 'Serious / Unusual Occurrence', short: 'SUO',
    category: 'department',
    rationale: 'Department + RIDOH — pediatric arrest, weapon contact, MCI, on-duty injury, restraint, DOA.',
  },
  eap_outreach: {
    id: 'eap_outreach', name: 'EAP Outreach', short: 'EAP',
    category: 'department',
    rationale: 'Critical-incident peer support trigger.',
  },
  me_referral: {
    id: 'me_referral', name: 'ME Referral', short: 'ME',
    category: 'department',
    rationale: 'RI medical examiner referral — DOA, suspicious circumstances, suspected homicide.',
  },
  audit_log: {
    id: 'audit_log', name: 'HIPAA Audit Log', short: 'AUD',
    category: 'department',
    rationale: 'Per-record access trail (open, edit, sign, transmit) — HIPAA §164.312(b).',
  },
  comstar_billing: {
    id: 'comstar_billing', name: 'Comstar Billing', short: 'CST',
    category: 'financial',
    rationale: 'The agency billing partner — HCPCS, modifier, ICD-10, loaded mileage, signed PCS.',
  },
  cms_pcs: {
    id: 'cms_pcs', name: 'CMS PCS', short: 'PCS',
    category: 'financial',
    rationale: 'Physician Certification Statement (non-emergency) — required ≤60 days for Medicare billing.',
  },
  aba_burn_referral: {
    id: 'aba_burn_referral', name: 'ABA Burn Packet', short: 'ABA',
    category: 'financial',
    rationale: 'American Burn Association transfer packet — accompanies handoff to burn center.',
  },
};

/* ============================================================
 * STAGES — top-down sequence the EMT moves through
 * ============================================================ */

export type StageId =
  | 'cad_intake'
  | 'scene_safety'
  | 'patient_identity'
  | 'history_pmhx'
  | 'allergies'
  | 'home_meds'
  | 'chief_complaint'
  | 'primary_assessment'
  | 'vitals'
  | 'secondary_assessment'
  | 'protocol_activation'
  | 'interventions'
  | 'trauma_special'
  | 'disposition'
  | 'mileage'
  | 'transfer_of_care'
  | 'narrative'
  | 'qa_review'
  | 'state_submission'
  | 'billing'
  | 'post';

export interface Stage {
  id: StageId;
  name: string;
  /** Plain-language description for the visualization. */
  description: string;
  /** Phase grouping for visual color band. */
  phase: 'pre_contact' | 'patient_identity' | 'assessment' | 'treatment' | 'handoff' | 'post_run';
}

export const STAGES: Stage[] = [
  { id: 'cad_intake',          name: 'CAD / PSAP Intake',          phase: 'pre_contact',
    description: 'Run number, incident type, address, units, dispatch reason, PSAP/CAD/dispatch/en-route times. Auto-populated from CAD feed.' },
  { id: 'scene_safety',        name: 'Scene & Safety',             phase: 'pre_contact',
    description: 'Scene type, hazards, MCI flag, LE on scene, weapon present, hostile environment.' },
  { id: 'patient_identity',    name: 'Patient Identification',     phase: 'patient_identity',
    description: 'Name, DOB, gender, race, address, insurance, contact, ID scan provenance.' },
  { id: 'history_pmhx',        name: 'PMHx & Surgical Hx',         phase: 'patient_identity',
    description: 'Chronic conditions (HTN, DM, CAD, COPD, CHF, CKD), prior surgeries, last meal, last menstrual.' },
  { id: 'allergies',           name: 'Allergies',                  phase: 'patient_identity',
    description: 'Drug / food / environmental, severity (anaphylaxis flag).' },
  { id: 'home_meds',           name: 'Home Medications',           phase: 'patient_identity',
    description: 'Prescribed Rx with RxNorm coding + auto-class flags (anticoagulant, hypoglycemic, opioid, beta-blocker).' },
  { id: 'chief_complaint',     name: 'Chief Complaint & Impression', phase: 'assessment',
    description: 'Patient-reported chief complaint, provider primary + secondary impressions. Drives protocol activation.' },
  { id: 'primary_assessment',  name: 'Primary Assessment',         phase: 'assessment',
    description: 'ABCDE, AVPU/GCS, immediate life threats, position found.' },
  { id: 'vitals',              name: 'Vital Signs (initial + serial)', phase: 'assessment',
    description: 'BP, pulse, respirations, SpO2, EtCO2, temp, BGL, pain score. First set + serial.' },
  { id: 'secondary_assessment', name: 'Secondary Assessment',      phase: 'assessment',
    description: 'Head-to-toe by system: head/neck, chest, abdomen, pelvis, extremities, neuro, skin, back.' },
  { id: 'protocol_activation', name: 'Protocol Activation',        phase: 'treatment',
    description: 'RI Statewide Protocol selected — drives intervention menu, dose pre-fill, scope filter.' },
  { id: 'interventions',       name: 'Interventions',              phase: 'treatment',
    description: 'IV/IO access, medications administered, procedures (12-lead, splinting, BVM, intubation, etc.).' },
  { id: 'trauma_special',      name: 'Trauma & Special Sets',      phase: 'treatment',
    description: 'Conditional: Trauma + sub-sets (MVC/DOT crash, fall, assault, burn, penetrating, peds), Stroke/LAMS, STEMI, behavioral, OB, restraint.' },
  { id: 'disposition',         name: 'Disposition',                phase: 'handoff',
    description: 'Transport decision, destination + reason, refusal, AMA, DOA, transferred to other service.' },
  { id: 'mileage',             name: 'Loaded Mileage',             phase: 'handoff',
    description: 'Loaded miles to destination — automatic from device GPS or manual entry.' },
  { id: 'transfer_of_care',    name: 'Transfer of Care',           phase: 'handoff',
    description: 'Receiving clinician name, MD/RN role, signature, time. Hospital ToC packet generated.' },
  { id: 'narrative',           name: 'Narrative Construction',     phase: 'post_run',
    description: 'DCHARTE / SBAR / SOAP / DISPATCH assembled from upstream structured data per Protocol 1.02.' },
  { id: 'qa_review',           name: 'QA Review',                  phase: 'post_run',
    description: 'Supervisor sign-off; EMT + QA digital signatures; lock the record.' },
  { id: 'state_submission',    name: 'State Submission',           phase: 'post_run',
    description: 'NEMSIS XML → RIEMSIS API. Fire-only path → NERIS post.' },
  { id: 'billing',             name: 'Billing Export',             phase: 'post_run',
    description: 'HCPCS + modifier + ICD-10 + loaded miles + signed PCS → the agency billing partner.' },
  { id: 'post',                name: 'Final Post & Lock',          phase: 'post_run',
    description: 'Record locked; access becomes audit-only; amendments require formal addendum.' },
];

/* ============================================================
 * CAPTURES — every data point and what it feeds
 * ============================================================ */

export interface Capture {
  /** Internal field key (matches EPCR / Incident schema where possible). */
  id: string;
  /** Human label for the visualization. */
  label: string;
  stage: StageId;
  /** NEMSIS element IDs this capture populates. */
  nemsis: string[];
  /** Downstream consumers fed by this capture. */
  feeds: ConsumerId[];
  /**
   * Other captures whose value pre-fills this one. Lets the
   * "captured once, satisfies all" view trace upstream sources.
   */
  prefilledBy?: string[];
  /** Free-text note about cardinality (one-time vs serial). */
  cardinality?: 'one_shot' | 'serial' | 'multi_select';
  /** Conditional: only required when this set is active. */
  gatedBy?: ConditionalSetId;
  /** Conditional: only required when this form is triggered. */
  triggers?: TriggeredFormId[];
}

export const CAPTURES: Capture[] = [
  /* ---- CAD / PSAP intake ---- */
  { id: 'incident.run_number', label: 'Run / Incident Number', stage: 'cad_intake',
    nemsis: ['eRecord.01'],
    feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing', 'audit_log'] },
  { id: 'incident.address', label: 'Incident Address', stage: 'cad_intake',
    nemsis: ['eScene.21', 'eScene.19'],
    feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing', 'audit_log', 'neris_fire'] },
  { id: 'incident.dispatch_reason', label: 'Dispatch Reason / Nature Code', stage: 'cad_intake',
    nemsis: ['eDispatch.01'],
    feeds: ['nemsis_xml', 'protocol_engine', 'narrative_engine'] },
  { id: 'incident.units', label: 'Units Responding', stage: 'cad_intake',
    nemsis: ['eResponse.05', 'eResponse.13'],
    feeds: ['nemsis_xml', 'comstar_billing', 'neris_fire'] },
  { id: 'incident.psap_time', label: 'PSAP / 911 Received', stage: 'cad_intake',
    nemsis: ['eTimes.01'],
    feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'] },
  { id: 'incident.dispatch_time', label: 'Dispatched', stage: 'cad_intake',
    nemsis: ['eTimes.03'],
    feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'] },
  { id: 'incident.en_route_time', label: 'En Route', stage: 'cad_intake',
    nemsis: ['eTimes.05'],
    feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'] },
  { id: 'incident.on_scene_time', label: 'On Scene', stage: 'cad_intake',
    nemsis: ['eTimes.06'],
    feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'] },
  { id: 'incident.types', label: 'Incident Types (EMS / Fire)', stage: 'cad_intake',
    nemsis: [],
    feeds: ['nemsis_xml', 'neris_fire'],
    triggers: ['neris_fire'] },

  /* ---- Scene safety ---- */
  { id: 'scene.scene_type', label: 'Scene Type', stage: 'scene_safety',
    nemsis: ['eScene.09'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine'] },
  { id: 'scene.hazards', label: 'Scene Hazards', stage: 'scene_safety',
    nemsis: ['eScene.06'],
    feeds: ['nemsis_xml', 'narrative_engine', 'serious_unusual'] },
  { id: 'scene.le_on_scene', label: 'LE On Scene', stage: 'scene_safety',
    nemsis: ['eScene.07'],
    feeds: ['nemsis_xml', 'narrative_engine'] },
  { id: 'scene.mci_flag', label: 'MCI Triage Activated', stage: 'scene_safety',
    nemsis: ['eScene.07', 'eScene.08'],
    feeds: ['nemsis_xml', 'serious_unusual', 'eap_outreach'],
    gatedBy: 'mass_casualty' },
  { id: 'scene.weapon_present', label: 'Weapon Present', stage: 'scene_safety',
    nemsis: [],
    feeds: ['serious_unusual', 'narrative_engine'] },

  /* ---- Patient identity ---- */
  { id: 'patient.first_name', label: 'First Name', stage: 'patient_identity',
    nemsis: ['ePatient.02'],
    feeds: ['nemsis_xml', 'riemsis_api', 'comstar_billing', 'toc_handoff', 'narrative_engine', 'audit_log'] },
  { id: 'patient.last_name', label: 'Last Name', stage: 'patient_identity',
    nemsis: ['ePatient.03'],
    feeds: ['nemsis_xml', 'riemsis_api', 'comstar_billing', 'toc_handoff', 'narrative_engine', 'audit_log'] },
  { id: 'patient.dob', label: 'Date of Birth', stage: 'patient_identity',
    nemsis: ['ePatient.17'],
    feeds: ['nemsis_xml', 'riemsis_api', 'comstar_billing', 'protocol_engine', 'narrative_engine', 'audit_log'] },
  { id: 'patient.gender', label: 'Gender', stage: 'patient_identity',
    nemsis: ['ePatient.13'],
    feeds: ['nemsis_xml', 'riemsis_api', 'comstar_billing'] },
  { id: 'patient.race', label: 'Race / Ethnicity', stage: 'patient_identity',
    nemsis: ['ePatient.14', 'ePatient.15'],
    feeds: ['nemsis_xml', 'ridoh_surveillance'] },
  { id: 'patient.address', label: 'Patient Home Address', stage: 'patient_identity',
    nemsis: ['ePatient.07', 'ePatient.08', 'ePatient.09', 'ePatient.10'],
    feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff'] },
  { id: 'patient.insurance', label: 'Insurance / Payer', stage: 'patient_identity',
    nemsis: ['ePayment.01', 'ePayment.10'],
    feeds: ['comstar_billing', 'cms_pcs'] },
  { id: 'patient.weight_kg', label: 'Weight (kg)', stage: 'patient_identity',
    nemsis: ['ePatient.20'],
    feeds: ['nemsis_xml', 'protocol_engine'],
    cardinality: 'one_shot' },

  /* ---- PMHx + allergies + meds ---- */
  { id: 'history.pmhx', label: 'Past Medical History', stage: 'history_pmhx',
    nemsis: ['eHistory.08'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine', 'toc_handoff'],
    cardinality: 'multi_select' },
  { id: 'history.alcohol_drug_indicators', label: 'Alcohol / Drug Indicators', stage: 'history_pmhx',
    nemsis: ['eHistory.17'],
    feeds: ['nemsis_xml', 'ridoh_surveillance', 'narrative_engine'],
    gatedBy: 'overdose' },
  { id: 'allergies.list', label: 'Allergies (drug / food / env)', stage: 'allergies',
    nemsis: ['eHistory.06'],
    feeds: ['nemsis_xml', 'protocol_engine', 'narrative_engine', 'toc_handoff'],
    cardinality: 'multi_select' },
  { id: 'meds.home_rx', label: 'Home Medications (RxNorm)', stage: 'home_meds',
    nemsis: ['eHistory.12'],
    feeds: ['nemsis_xml', 'protocol_engine', 'narrative_engine', 'toc_handoff'],
    cardinality: 'multi_select' },
  { id: 'meds.anticoagulant_flag', label: 'Anticoagulant Flag', stage: 'home_meds',
    nemsis: [],
    feeds: ['protocol_engine', 'narrative_engine', 'toc_handoff', 'trauma_alert'],
    prefilledBy: ['meds.home_rx'] },
  { id: 'meds.hypoglycemic_flag', label: 'Hypoglycemic Flag', stage: 'home_meds',
    nemsis: [],
    feeds: ['protocol_engine', 'narrative_engine', 'toc_handoff'],
    prefilledBy: ['meds.home_rx'] },

  /* ---- Chief complaint + impression + protocol ---- */
  { id: 'situation.chief_complaint', label: 'Chief Complaint', stage: 'chief_complaint',
    nemsis: ['eSituation.04'],
    feeds: ['nemsis_xml', 'protocol_engine', 'narrative_engine', 'comstar_billing'] },
  { id: 'situation.primary_impression', label: 'Provider Primary Impression', stage: 'chief_complaint',
    nemsis: ['eSituation.11'],
    feeds: ['nemsis_xml', 'protocol_engine', 'narrative_engine', 'comstar_billing', 'pci_alert', 'stroke_alert', 'trauma_alert', 'serious_unusual'] },
  { id: 'situation.secondary_impressions', label: 'Secondary Impressions', stage: 'chief_complaint',
    nemsis: ['eSituation.12'],
    feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    cardinality: 'multi_select' },
  { id: 'situation.possible_injury', label: 'Possible Injury (Y/N/Unknown)', stage: 'chief_complaint',
    nemsis: ['eSituation.02'],
    feeds: ['nemsis_xml'],
    triggers: [] },

  /* ---- Primary assessment ---- */
  { id: 'assessment.gcs', label: 'GCS (Eye / Verbal / Motor)', stage: 'primary_assessment',
    nemsis: ['eVitals.19', 'eVitals.20', 'eVitals.21', 'eVitals.23'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine', 'trauma_alert', 'stroke_alert', 'toc_handoff'],
    cardinality: 'serial' },
  { id: 'assessment.avpu', label: 'AVPU', stage: 'primary_assessment',
    nemsis: [],
    feeds: ['narrative_engine', 'protocol_engine'] },
  { id: 'assessment.airway', label: 'Airway Status', stage: 'primary_assessment',
    nemsis: ['eExam.10'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine'] },

  /* ---- Vitals ---- */
  { id: 'vitals.bp', label: 'Blood Pressure (sys/dia)', stage: 'vitals',
    nemsis: ['eVitals.06', 'eVitals.07'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine', 'toc_handoff', 'trauma_alert'],
    cardinality: 'serial' },
  { id: 'vitals.pulse', label: 'Pulse Rate', stage: 'vitals',
    nemsis: ['eVitals.10'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine', 'toc_handoff'],
    cardinality: 'serial' },
  { id: 'vitals.respirations', label: 'Respiratory Rate', stage: 'vitals',
    nemsis: ['eVitals.14'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine', 'toc_handoff'],
    cardinality: 'serial' },
  { id: 'vitals.spo2', label: 'SpO2', stage: 'vitals',
    nemsis: ['eVitals.12'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine', 'toc_handoff'],
    cardinality: 'serial' },
  { id: 'vitals.etco2', label: 'EtCO2', stage: 'vitals',
    nemsis: ['eVitals.16'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine', 'toc_handoff'],
    cardinality: 'serial' },
  { id: 'vitals.bgl', label: 'Blood Glucose', stage: 'vitals',
    nemsis: ['eVitals.18'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine', 'stroke_alert', 'toc_handoff'],
    cardinality: 'serial' },
  { id: 'vitals.pain_score', label: 'Pain Score (0-10)', stage: 'vitals',
    nemsis: ['eVitals.27'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine'],
    cardinality: 'serial' },

  /* ---- Secondary assessment + special sets ---- */
  { id: 'exam.head_to_toe', label: 'Head-to-Toe Exam Findings', stage: 'secondary_assessment',
    nemsis: ['eExam.10', 'eExam.11', 'eExam.12', 'eExam.13', 'eExam.14', 'eExam.15', 'eExam.16', 'eExam.17', 'eExam.18'],
    feeds: ['nemsis_xml', 'narrative_engine', 'protocol_engine', 'trauma_alert', 'toc_handoff'],
    cardinality: 'multi_select' },
  { id: 'stroke.lams_score', label: 'LAMS Score (Stroke 0-5)', stage: 'secondary_assessment',
    nemsis: ['eVitals.29'],
    feeds: ['nemsis_xml', 'narrative_engine', 'stroke_alert', 'toc_handoff'],
    gatedBy: 'stroke' },
  { id: 'stroke.last_known_well', label: 'Last Known Well Time', stage: 'secondary_assessment',
    nemsis: ['eSituation.18'],
    feeds: ['nemsis_xml', 'narrative_engine', 'stroke_alert', 'toc_handoff'],
    gatedBy: 'stroke' },
  { id: 'stroke.cincinnati', label: 'Cincinnati Stroke Scale (3 components)', stage: 'secondary_assessment',
    nemsis: ['eVitals.30'],
    feeds: ['nemsis_xml', 'narrative_engine', 'stroke_alert', 'toc_handoff'],
    gatedBy: 'stroke' },
  { id: 'stemi.first_12_lead', label: 'First 12-Lead Time / Interpretation', stage: 'secondary_assessment',
    nemsis: ['eVitals.31'],
    feeds: ['nemsis_xml', 'narrative_engine', 'pci_alert', 'toc_handoff'],
    gatedBy: 'stemi' },
  { id: 'trauma.injury_causes', label: 'Cause of Injury', stage: 'trauma_special',
    nemsis: ['eInjury.01'],
    feeds: ['nemsis_xml', 'narrative_engine', 'trauma_alert'],
    gatedBy: 'trauma',
    cardinality: 'multi_select' },
  { id: 'trauma.triage_red', label: 'ACS Red Triage Criteria', stage: 'trauma_special',
    nemsis: ['eInjury.03'],
    feeds: ['nemsis_xml', 'narrative_engine', 'trauma_alert'],
    gatedBy: 'trauma',
    cardinality: 'multi_select' },
  { id: 'trauma.triage_yellow', label: 'ACS Yellow Triage Criteria', stage: 'trauma_special',
    nemsis: ['eInjury.04'],
    feeds: ['nemsis_xml', 'narrative_engine', 'trauma_alert'],
    gatedBy: 'trauma',
    cardinality: 'multi_select' },
  { id: 'crash.vehicle_role', label: 'Vehicle Role (driver/passenger/ped)', stage: 'trauma_special',
    nemsis: ['eInjury.07'],
    feeds: ['nemsis_xml', 'narrative_engine', 'trauma_alert'],
    triggers: ['dot_crash'],
    gatedBy: 'trauma_mvc' },
  { id: 'crash.restraint_use', label: 'Restraint Use', stage: 'trauma_special',
    nemsis: ['eInjury.08'],
    feeds: ['nemsis_xml', 'narrative_engine', 'trauma_alert'],
    triggers: ['dot_crash'],
    gatedBy: 'trauma_mvc' },
  { id: 'crash.airbag', label: 'Airbag Deployment', stage: 'trauma_special',
    nemsis: ['eInjury.09'],
    feeds: ['nemsis_xml', 'narrative_engine'],
    triggers: ['dot_crash'],
    gatedBy: 'trauma_mvc' },
  { id: 'crash.intrusion', label: 'Intrusion Location + Depth', stage: 'trauma_special',
    nemsis: ['eInjury.10'],
    feeds: ['nemsis_xml', 'narrative_engine', 'trauma_alert'],
    triggers: ['dot_crash'],
    gatedBy: 'trauma_mvc' },
  { id: 'crash.ejection', label: 'Ejection / Entrapment / Death in Compartment', stage: 'trauma_special',
    nemsis: ['eInjury.11'],
    feeds: ['nemsis_xml', 'narrative_engine', 'trauma_alert', 'serious_unusual'],
    triggers: ['dot_crash'],
    gatedBy: 'trauma_mvc' },
  { id: 'burn.tbsa', label: 'Burn TBSA %', stage: 'trauma_special',
    nemsis: [],
    feeds: ['narrative_engine', 'trauma_alert', 'aba_burn_referral'],
    gatedBy: 'trauma_burn',
    triggers: ['aba_burn_referral'] },
  { id: 'restraint.justification', label: 'Restraint Justification + MD Contact', stage: 'trauma_special',
    nemsis: [],
    feeds: ['narrative_engine', 'serious_unusual'],
    gatedBy: 'restraint_use',
    triggers: ['serious_incident'] },

  /* ---- Protocol activation ---- */
  { id: 'protocol.id', label: 'Protocol Activated (RI v2026.01)', stage: 'protocol_activation',
    nemsis: ['eProtocols.01'],
    feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    prefilledBy: ['situation.primary_impression', 'situation.chief_complaint'],
    cardinality: 'multi_select' },
  { id: 'protocol.deviation', label: 'Protocol Deviation Reason', stage: 'protocol_activation',
    nemsis: [],
    feeds: ['narrative_engine', 'serious_unusual'] },

  /* ---- Interventions ---- */
  { id: 'iv.access', label: 'IV / IO Access (site, gauge, attempts)', stage: 'interventions',
    nemsis: ['eProcedures.03', 'eProcedures.05', 'eProcedures.06'],
    feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing', 'toc_handoff'],
    cardinality: 'multi_select' },
  { id: 'medications.administered', label: 'Medications Given (RxNorm + dose + route)', stage: 'interventions',
    nemsis: ['eMedications.01', 'eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06', 'eMedications.10'],
    feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing', 'toc_handoff'],
    cardinality: 'multi_select' },
  { id: 'procedures.performed', label: 'Procedures (12-lead, BVM, intubation, splint, etc.)', stage: 'interventions',
    nemsis: ['eProcedures.03', 'eProcedures.10', 'eProcedures.13'],
    feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing', 'toc_handoff'],
    cardinality: 'multi_select' },
  { id: 'cpr.events', label: 'CPR / Defibrillation Events', stage: 'interventions',
    nemsis: ['eArrest.09', 'eArrest.11', 'eArrest.12', 'eArrest.16'],
    feeds: ['nemsis_xml', 'narrative_engine', 'toc_handoff'],
    gatedBy: 'cardiac_arrest',
    cardinality: 'serial' },

  /* ---- Disposition + mileage + ToC ---- */
  { id: 'disposition.transport_decision', label: 'Transport Decision', stage: 'disposition',
    nemsis: ['eDisposition.27'],
    feeds: ['nemsis_xml', 'comstar_billing', 'narrative_engine', 'cms_pcs'] },
  { id: 'disposition.destination', label: 'Receiving Facility', stage: 'disposition',
    nemsis: ['eDisposition.01', 'eDisposition.02'],
    feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'pci_alert', 'stroke_alert', 'trauma_alert'] },
  { id: 'disposition.refusal_capacity', label: 'Refusal Capacity Assessment', stage: 'disposition',
    nemsis: ['eDisposition.28', 'eDisposition.30'],
    feeds: ['nemsis_xml', 'narrative_engine'],
    gatedBy: 'refusal' },
  { id: 'disposition.doa_pronouncement', label: 'DOA / Pronouncement Time', stage: 'disposition',
    nemsis: [],
    feeds: ['narrative_engine', 'me_referral', 'serious_unusual'],
    gatedBy: 'doa',
    triggers: ['me_referral'] },
  { id: 'mileage.loaded', label: 'Loaded Mileage', stage: 'mileage',
    nemsis: ['eDisposition.16'],
    feeds: ['nemsis_xml', 'comstar_billing'] },
  { id: 'toc.receiving_clinician', label: 'Receiving Clinician + Signature', stage: 'transfer_of_care',
    nemsis: ['eDisposition.30'],
    feeds: ['nemsis_xml', 'toc_handoff', 'audit_log'] },

  /* ---- Narrative + QA + post ---- */
  { id: 'narrative.text', label: 'Narrative (DCHARTE / SBAR / SOAP)', stage: 'narrative',
    nemsis: ['eNarrative.01'],
    feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff'],
    prefilledBy: ['*'] },
  { id: 'qa.emt_signature', label: 'EMT Signature + Attestation', stage: 'qa_review',
    nemsis: ['eRecord.01'],
    feeds: ['nemsis_xml', 'audit_log', 'comstar_billing'] },
  { id: 'qa.qa_signature', label: 'QA Officer Signature', stage: 'qa_review',
    nemsis: [],
    feeds: ['audit_log', 'serious_unusual'] },
  { id: 'submission.nemsis_xml', label: 'NEMSIS XML payload', stage: 'state_submission',
    nemsis: [],
    feeds: ['riemsis_api', 'audit_log'] },
  { id: 'submission.neris_payload', label: 'NERIS Fire JSON', stage: 'state_submission',
    nemsis: [],
    feeds: ['neris_fire', 'audit_log'],
    gatedBy: 'fire_response' },
  { id: 'billing.hcpcs', label: 'HCPCS + Modifier', stage: 'billing',
    nemsis: [],
    feeds: ['comstar_billing'],
    prefilledBy: ['disposition.transport_decision', 'mileage.loaded', 'situation.primary_impression'] },
  { id: 'billing.icd10', label: 'ICD-10 Codes', stage: 'billing',
    nemsis: ['eSituation.11', 'eSituation.12'],
    feeds: ['comstar_billing'],
    prefilledBy: ['situation.primary_impression', 'situation.secondary_impressions'] },
  { id: 'post.lock', label: 'Final Record Lock', stage: 'post',
    nemsis: [],
    feeds: ['audit_log'] },
];

/* ============================================================
 * Helpers
 * ============================================================ */

export function capturesByStage(stageId: StageId): Capture[] {
  return CAPTURES.filter((c) => c.stage === stageId);
}

export function capturesFeedingConsumer(consumerId: ConsumerId): Capture[] {
  return CAPTURES.filter((c) => c.feeds.includes(consumerId));
}

export function consumersForStage(stageId: StageId): ConsumerId[] {
  const set = new Set<ConsumerId>();
  for (const c of capturesByStage(stageId)) {
    for (const f of c.feeds) set.add(f);
  }
  return Array.from(set);
}

/**
 * For the redundancy-isolation panel: every capture grouped by the
 * number of consumers it feeds. The bigger the consumer count, the
 * more efficient the capture (one keystroke = many downstream forms).
 */
export function captureFanOutRanking(): Capture[] {
  return [...CAPTURES].sort((a, b) => b.feeds.length - a.feeds.length);
}
